import { apply, array, number, option, ord } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';

import { Amount, isZeroAmount } from '@mews-distributor/common';
import { CancellationFee } from '@mews-distributor/data-access';
import { PricingModeKey } from '@mews/enums';
import { getProp, getStringProp, values } from '@mews/fp';

import { getCategoryServiceId } from '../../../selectors/views/categories';
import { ApplicationState } from '../../../types';
import { WithCategoryId } from '../../../types/selectors/selectorPropTypes';
import { getEnterpriseBasicProductIds } from '../../entities/products/selectors';
import { getServiceEnterpriseId } from '../../entities/services/selectors';
import { getServiceEnterprisePricingModeKey } from '../../entities/services/viewSelectors';
import { getCategoriesOccupancyData } from '../../occupancy/categoriesOccupancies';
import {
    getEndDate,
    getEndTimeUtc,
    getStartDate,
    getStartTimeUtc,
    getVoucherCode,
} from '../../resourceOrder/selectors';
import { getPricingHash } from '../getPricingHash';
import { getPricingValueByHash } from '../selectors';
import { PricingRate } from '../types';

export const getCurrentServiceDateCategoryPricing = (state: ApplicationState, { categoryId }: WithCategoryId) => {
    const serviceId = getCategoryServiceId(state, { categoryId });
    if (serviceId == null) {
        return option.none;
    }
    return pipe(
        {
            startDate: option.fromNullable(getStartDate(state)),
            endDate: option.fromNullable(getEndDate(state)),
            enterpriseId: option.fromNullable(getServiceEnterpriseId(state, { serviceId })),
        },
        apply.sequenceS(option.Apply),
        option.flatMap(({ enterpriseId, endDate, startDate }) => {
            const voucherCode = getVoucherCode(state);
            const startTimeUtc = getStartTimeUtc(state);
            const endTimeUtc = getEndTimeUtc(state);
            /**
             * Make sure this uses the same selector as the one used in the API call to fetch the pricing.
             * Otherwise the pricing won't be found because we fetched it for different occupancies.
             */
            const occupancyData = getCategoriesOccupancyData(state, { serviceId });
            const productIds = getEnterpriseBasicProductIds(state, { enterpriseId });

            const pricingHash = getPricingHash({
                startDate: startTimeUtc ?? startDate,
                endDate: endTimeUtc ?? endDate,
                enterpriseId,
                occupancyData,
                voucherCode,
                categoryId,
                productIds,
            });

            return option.fromNullable(getPricingValueByHash(state, { pricingHash }));
        })
    );
};

export const getCurrentDateServiceCategoryRatesPricing = createSelector(
    getCurrentServiceDateCategoryPricing,
    flow(
        option.map((pricingValue) => pipe(pricingValue.rates, values)),
        option.getOrElse<PricingRate[]>(() => [])
    )
);

interface GetServiceCategoryLowestPrice {
    pricingModeKey: PricingModeKey;
    currencyCode: string;
}

interface WithCityTaxIncludedInPrice {
    cityTaxIncludedInPrice: boolean;
}

const getServiceCategoryLowestPrice =
    ({ currencyCode, pricingModeKey }: GetServiceCategoryLowestPrice) =>
    (pricingRates: PricingRate[]) =>
        pipe(
            pricingRates,
            array.map(({ price, cityTaxIncludedInPrice, sellLimitStatistic, feeProducts }) => {
                const priceKey = 'averageAmountPerFullTimeUnit';

                return option.fromNullable(
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    price[priceKey]?.[currencyCode]
                        ? {
                              ...price[priceKey][currencyCode],
                              cityTaxIncludedInPrice,
                              sellLimitStatistic,
                              feeProducts,
                          }
                        : null
                );
            }),
            array.compact,
            (x) => x,
            array.sortBy([
                pipe(
                    number.Ord,
                    ord.contramap((price: Amount & WithCityTaxIncludedInPrice) => {
                        if (pricingModeKey === PricingModeKey.Value) {
                            return -1;
                        }
                        return price[pricingModeKey] ?? Number.MAX_SAFE_INTEGER;
                    })
                ),
            ]),
            array.head
        );

const getCurrentDateServicePricing = (state: ApplicationState, { categoryIds }: { categoryIds: string[] }) =>
    pipe(
        categoryIds,
        array.map((categoryId) => getCurrentServiceDateCategoryPricing(state, { categoryId })),
        array.compact
    );

const getCurrentDateServicePricingRates = createSelector(
    getCurrentDateServicePricing,
    array.map((pricingValue) => pricingValue.rates)
);

export const getCurrentDateServiceLowestPrice = createSelector(
    getCurrentDateServicePricingRates,
    getServiceEnterprisePricingModeKey,
    getStringProp('currencyCode'),

    (datePricing, pricingModeKeyOption, currencyCode) =>
        pipe(
            {
                dateServicePricing: option.of(datePricing),
                pricingModeKey: pricingModeKeyOption,
            },
            apply.sequenceS(option.Apply),
            option.flatMap(({ pricingModeKey, dateServicePricing }) =>
                pipe(
                    dateServicePricing,
                    array.flatMap(values),
                    getServiceCategoryLowestPrice({ currencyCode, pricingModeKey })
                )
            )
        )
);
const hasFreeCancellation = (fees: Array<CancellationFee>) =>
    pipe(
        fees,
        array.some((fee) => isZeroAmount(fee.amount))
    );

const filterRefundableRates = (shouldFilter = false) =>
    array.filter<PricingRate>((rate) => (shouldFilter ? hasFreeCancellation(rate.cancellationFees) : true));

export const getCurrentDateServiceCategoryLowestPrice = createSelector(
    getCurrentServiceDateCategoryPricing,
    getServiceEnterprisePricingModeKey,
    getStringProp('currencyCode'),
    getProp<boolean | undefined>()('filterOnlyRefundable'),
    (servicePricingOption, pricingModeKeyOption, currencyCode, filterOnlyRefundable) =>
        pipe(
            {
                servicePricing: servicePricingOption,
                pricingModeKey: pricingModeKeyOption,
            },
            apply.sequenceS(option.Apply),
            option.flatMap(({ servicePricing, pricingModeKey }) =>
                pipe(
                    servicePricing.rates,
                    values,
                    filterRefundableRates(Boolean(filterOnlyRefundable)),
                    getServiceCategoryLowestPrice({ currencyCode, pricingModeKey })
                )
            )
        )
);

export const getCurrentDateServiceCategoryRateIds = createSelector(
    getCurrentDateServicePricingRates,
    (serviceCategoryPricing) => Object.keys(serviceCategoryPricing?.[0] ?? {})
);
